/**
 * Bundle product page configurator component.
 *
 * Variant of ConfiguratorPdp for bundle PDPs. Identical behaviour except the
 * "Continue" button is replaced with "Add to Cart", which adds the resolved
 * cover variant, each independently-fitted configurable product's own
 * matched variant, the resolved FastTrack variant (if present), and all flat
 * secondary bundle products to the cart in one request (tagged with the
 * merchant-configurable discount line-item property) and then opens the
 * Rebuy Smart Cart.
 *
 * Secondary variant IDs are passed via data-secondary-variant-ids (comma-
 * separated). Additional independently-fitted configurable products are
 * passed via data-configurable-products (JSON array of
 * {variantId, handle, collectionHandle}) — each one's collectionHandle is
 * looked up in the same configurator-urls.json map the truck selection
 * already resolved, and its ?variant= substituted for the default variantId,
 * same as bundle-truck-selector-js_v2 did per configurable product. FastTrack
 * is resolved server-side (its variant doesn't depend on truck fit) and
 * passed via data-fasttrack-variant-id. The cart line-item property key/value
 * come from data-discount-key/data-discount-value. All set in
 * configurator-bundle-pdp.liquid.
 */

// Display names and fallback PDP URLs for alternate bundles shown in the unavailable state.
// Keyed by collection handle (the value set in configurator-bundle-pdp.liquid).
const PDP_BUNDLE_PRODUCT_LABELS = {
  'wingman-slider-bin-configurable': 'Fold & Roll Bundle',
  'cross-bin-8-configurable':        'Cross & Grab-Go Bundle',
  'cross-bin-13-configurable':       'Pack Mule Bundle',
};

const PDP_BUNDLE_PRODUCT_URLS = {
  'wingman-slider-bin-configurable': '/products/fold-roll-bundle',
  'cross-bin-8-configurable':        '/products/cross-grab-go-bundle',
  'cross-bin-13-configurable':       '/products/pack-mule-bundle',
};

// Only these bundles show alternate links; each shows the other two.
const PDP_BUNDLE_ALTERNATES = {
  'wingman-slider-bin-configurable': ['cross-bin-8-configurable', 'cross-bin-13-configurable'],
  'cross-bin-8-configurable':        ['wingman-slider-bin-configurable', 'cross-bin-13-configurable'],
  'cross-bin-13-configurable':       ['wingman-slider-bin-configurable', 'cross-bin-8-configurable'],
};

class ConfiguratorBundlePdp extends (window.ConfiguratorBase || HTMLElement) {
  #collectionHandle = '';
  #continueUrl = null;
  #baseChildUrl = null;
  #colorOptionIndex = -1;
  #cleatsOptionIndex = -1;
  #variantMap = {};
  #secondaryVariantIds = [];
  #configurableProducts = [];
  #fasttrackVariantId = null;
  #discountKey = '_CustomBundleDiscount';
  #discountValue = 'true';
  #lastUrls = {};

  getSource() { return 'bundle-pdp'; }

  cacheAdditionalRefs() {
    this.#collectionHandle = this.getAttribute('data-collection-handle') || '';
    this.#colorOptionIndex = this.#parseOptionIndex('data-color-option-index');
    this.#cleatsOptionIndex = this.#parseOptionIndex('data-cleats-option-index');
    this.#loadVariantMap();
    this.#loadSecondaryVariantIds();
    this.#loadConfigurableProducts();
    this.#loadFasttrackVariantId();
    this.#loadDiscountProperty();

    this._formView = this.querySelector('[data-ref="formView"]');
    this._confirmedView = this.querySelector('[data-ref="confirmedView"]');
    this._unavailableView = this.querySelector('[data-ref="unavailableView"]');
    this._confirmedTruckValue = this.querySelector('[data-ref="confirmedTruckValue"]');
    this._continueLink = this.querySelector('[data-ref="continueLink"]');
    this._notMyTruckLink = this.querySelector('[data-ref="notMyTruckLink"]');
    this._unavailableTruckValue = this.querySelector('[data-ref="unavailableTruckValue"]');
    this._notMyTruckLink2 = this.querySelector('[data-ref="notMyTruckLink2"]');
    this._alternateLinksContainer = this.querySelector('[data-ref="alternateLinksContainer"]');
  }

  bindAdditionalEvents() {
    this._continueLink?.addEventListener('click', () => this.#addToCart());
    this._notMyTruckLink?.addEventListener('click', (e) => {
      e.preventDefault();
      this._clearTruck();
    });
    this._notMyTruckLink2?.addEventListener('click', (e) => {
      e.preventDefault();
      this._clearTruck();
    });
    document.addEventListener('variantupdated', this.#handleVariantChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    document.removeEventListener('variantupdated', this.#handleVariantChange);
  }

  async onSelectionComplete(selection, urls) {
    this._saveTruck(selection, urls);
    this.#lastUrls = urls || {};
    document.dispatchEvent(new CustomEvent('configurator:update', { detail: { selection, urls } }));
    const childUrl = this.#collectionHandle ? urls[this.#collectionHandle] : null;
    if (childUrl) {
      this.#showAvailableState(selection, childUrl);
    } else {
      this.#showUnavailableState(selection, urls);
    }
  }

  onReset() {
    this.#lastUrls = {};
    this.#showFormState();
  }

  async onRestored(saved, urls) {
    this.#lastUrls = urls || {};
    const childUrl = this.#collectionHandle ? urls[this.#collectionHandle] : null;
    if (childUrl) {
      this.#showAvailableState(saved, childUrl);
    } else {
      this.#showUnavailableState(saved, urls);
    }
  }

  // -- Add to Cart --

  async #addToCart() {
    // #collectionHandle/pdp_collection_handle is only used here as a gate
    // (is a truck resolved at all for this bundle's legacy 3-bundle
    // classification?) and, elsewhere, to drive the "Other Available
    // Bundles" cross-sell links. Its own resolved variant is NOT added to
    // cart — every truck-fitted item that actually belongs in this bundle
    // is already listed (and independently matched) via configurable_products,
    // so adding this too would duplicate whichever configurable product maps
    // to the same collection.
    const targetUrl = this.#baseChildUrl
      ? this.#resolveUrl(this.#baseChildUrl)
      : this.#continueUrl;
    if (!targetUrl) return;

    const configurableVariantIds = this.#resolveConfigurableProductVariantIds();
    const ids = [
      ...configurableVariantIds.map(String),
      ...(this.#fasttrackVariantId ? [String(this.#fasttrackVariantId)] : []),
      ...this.#secondaryVariantIds.map(String),
    ].filter(Boolean);
    const items = ids.map((id) => ({
      id: Number(id),
      quantity: 1,
      properties: { [this.#discountKey]: this.#discountValue },
    }));

    this._continueLink.disabled = true;
    this._continueLink.textContent = 'Adding…';

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!response.ok) throw new Error('Cart update failed.');
      if (window.Rebuy) {
        if (typeof window.Rebuy.Cart?.update === 'function') {
          window.Rebuy.Cart.update();
        } else if (typeof window.Rebuy.refreshCart === 'function') {
          window.Rebuy.refreshCart();
        } else if (typeof window.Rebuy.SmartCart?.update === 'function') {
          window.Rebuy.SmartCart.update();
        } else {
          console.warn('[ConfiguratorBundlePdp] Rebuy cart update method not found.');
        }
        window.Rebuy.SmartCart?.show();
      }
    } catch (err) {
      console.error('[ConfiguratorBundlePdp] Cart error:', err);
    } finally {
      this._continueLink.disabled = false;
      this._continueLink.textContent = 'Add to Cart';
    }
  }

  // -- State transitions --

  #showFormState() {
    this.#baseChildUrl = null;
    this.#continueUrl = null;
    this._formView?.removeAttribute('hidden');
    this._confirmedView?.setAttribute('hidden', '');
    this._unavailableView?.setAttribute('hidden', '');
  }

  #showAvailableState(selection, childUrl) {
    const display = `${selection.year} | ${selection.make} | ${selection.model} | ${selection.bed} Bed`;
    if (this._confirmedTruckValue) this._confirmedTruckValue.textContent = display;
    this.#baseChildUrl = childUrl;
    this.#continueUrl = this.#resolveUrl(this.#baseChildUrl);
    this._formView?.setAttribute('hidden', '');
    this._confirmedView?.removeAttribute('hidden');
    this._unavailableView?.setAttribute('hidden', '');
  }

  #showUnavailableState(selection, urls) {
    const display = `${selection.year} | ${selection.make} | ${selection.model} | ${selection.bed} Bed`;
    if (this._unavailableTruckValue) this._unavailableTruckValue.textContent = display;
    this.#baseChildUrl = null;
    this.#continueUrl = null;
    this.#buildAlternateLinks(urls);
    this._formView?.setAttribute('hidden', '');
    this._confirmedView?.setAttribute('hidden', '');
    this._unavailableView?.removeAttribute('hidden');
  }

  #buildAlternateLinks(urls) {
    const container = this._alternateLinksContainer;
    if (!container) return;
    const alternateHandles = PDP_BUNDLE_ALTERNATES[this.#collectionHandle] || [];
    const links = alternateHandles
      .filter((handle) => urls[handle])
      .map((handle) => ({
        label: PDP_BUNDLE_PRODUCT_LABELS[handle] || handle,
        href: urls[handle],
      }));
    container.innerHTML = links
      .map((l) => `<a href="${l.href}" class="configurator-pdp__alt-link">Shop ${l.label}</a>`)
      .join('');
    const altProducts = container.closest('.configurator-pdp__alt-products');
    altProducts?.toggleAttribute('hidden', links.length === 0);
  }

  // -- Variant resolution --

  #resolveUrl(baseUrl) {
    if (!baseUrl) return null;
    const { finish } = this.#getCurrentOptions();
    if (!finish) return baseUrl;

    let parsedUrl;
    try {
      parsedUrl = new URL(baseUrl, window.location.origin);
    } catch {
      return baseUrl;
    }

    const pathParts = parsedUrl.pathname.split('/').filter(Boolean);
    const productsIndex = pathParts.indexOf('products');
    if (productsIndex === -1 || !pathParts[productsIndex + 1]) return baseUrl;

    const handle = pathParts[productsIndex + 1];
    const currentVariantId = parsedUrl.searchParams.get('variant');
    if (!currentVariantId) return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;

    let siblingMap = this.#variantMap[handle]?.[currentVariantId];
    if (!siblingMap) {
      const productMap = this.#variantMap[handle];
      if (productMap) {
        const finishPrefix = `${this.#normalizeOptionValue(finish)}|`;
        for (const map of Object.values(productMap)) {
          if (Object.keys(map).some((k) => k.startsWith(finishPrefix))) {
            siblingMap = map;
            break;
          }
        }
      }
      if (!siblingMap) {
        console.warn('[ConfiguratorBundlePdp] Missing sibling map for handle/variant.', { handle, currentVariantId, finish });
        return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
      }
    }

    const cleatsFromVariant = this.#getCleatsFromVariantId(siblingMap, currentVariantId);
    const cleats = this.#isCleatsExplicitlySelected()
      ? (this.#getOptionValue(this.#cleatsOptionIndex) || this.#getOptionValue(1))
      : (cleatsFromVariant ?? '');

    const variantId = this.#resolveMappedVariantId(siblingMap, finish, cleats);
    if (!variantId) {
      console.warn('[ConfiguratorBundlePdp] No variant mapping for selected finish/cleats.', { handle, currentVariantId, finish, cleats });
      return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
    }

    parsedUrl.searchParams.set('variant', String(variantId));
    if (this.#isCleatsExplicitlySelected()) {
      parsedUrl.searchParams.set('allowCleatPreselect', 'true');
    }
    return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
  }

  #getCurrentOptions() {
    const finish = this.#getOptionValue(this.#colorOptionIndex);
    const cleats = this.#getOptionValue(this.#cleatsOptionIndex);
    return {
      finish: finish || this.#getOptionValue(0),
      cleats: cleats || this.#getOptionValue(1),
    };
  }

  #getOptionValue(optionIndex) {
    if (optionIndex < 0) return '';
    const input = document.getElementById(`SingleOptionSelector-${optionIndex}`);
    return this.#normalizeOptionValue(input?.value || '');
  }

  #normalizeOptionValue(value) {
    return String(value || '').trim().replace(/\s+/g, ' ');
  }

  #isCleatsExplicitlySelected() {
    const index = this.#cleatsOptionIndex >= 0 ? this.#cleatsOptionIndex : 1;
    const wrapper = document.querySelector(`.button-option-wrapper[data-option-index="${index}"]`);
    if (!wrapper) return true;
    const selectedBtn = wrapper.querySelector('.button-option.selected');
    return selectedBtn ? !selectedBtn.classList.contains('masked-selection') : false;
  }

  #getCleatsFromVariantId(siblingMap, variantId) {
    for (const [key, id] of Object.entries(siblingMap)) {
      if (String(id) === variantId) {
        const pipeIndex = key.indexOf('|');
        return pipeIndex >= 0 ? key.slice(pipeIndex + 1) : '';
      }
    }
    return null;
  }

  #resolveMappedVariantId(siblingMap, finish, cleats) {
    const lookupKeys = this.#buildLookupKeys(finish, cleats);
    for (const key of lookupKeys) {
      if (siblingMap[key]) return siblingMap[key];
    }
    if (!cleats) {
      const prefix = `${this.#normalizeOptionValue(finish)}|`;
      const finishMatches = Object.entries(siblingMap).filter(([key]) => key.startsWith(prefix));
      if (finishMatches.length >= 1) return finishMatches[0][1];
    }
    return null;
  }

  #buildLookupKeys(finish, cleats) {
    const normalizedFinish = this.#normalizeOptionValue(finish);
    if (!normalizedFinish) return [];
    return this.#getCleatsCandidates(cleats).map(
      (cleatValue) => `${normalizedFinish}|${cleatValue}`
    );
  }

  #getCleatsCandidates(cleats) {
    const normalized = this.#normalizeOptionValue(cleats);
    if (!normalized) return [''];
    const lower = normalized.toLowerCase();
    const candidates = [normalized];
    if (lower === 'yes' || lower === 'preinstalled' || lower === 'installed') {
      candidates.push('Installed', 'Preinstalled', 'Yes');
    }
    if (lower === 'no' || lower === 'loose') {
      candidates.push('Loose', 'No');
    }
    return [...new Set(candidates)];
  }

  // -- Independently-fitted configurable products --

  /**
   * Each configurable product (besides the bundle's own primary selection)
   * gets its own variant matched against the current truck via its own
   * collectionHandle, exactly like bundle-truck-selector-js_v2 resolved a
   * variant per configurable product from its own data-collection lookup —
   * just against the static configurator-urls.json map instead of a live API
   * call. Falls back to the product's default variant if the truck isn't
   * available in that product's collection (matches the old "still add the
   * default variant" behavior for out-of-fitment configurable products).
   */
  #resolveConfigurableProductVariantIds() {
    return this.#configurableProducts.map((cp) => {
      const url = cp.collectionHandle ? this.#lastUrls[cp.collectionHandle] : null;
      if (!url) return cp.variantId;
      try {
        const variant = new URL(url, window.location.origin).searchParams.get('variant');
        return variant ? Number(variant) : cp.variantId;
      } catch {
        return cp.variantId;
      }
    }).filter((id) => id > 0);
  }

  // -- Setup helpers --

  #loadVariantMap() {
    const el = document.getElementById('configurator-pdp-variant-map');
    if (!el) return;
    try { this.#variantMap = JSON.parse(el.textContent) || {}; } catch { /* ignore */ }
  }

  #loadSecondaryVariantIds() {
    const raw = this.getAttribute('data-secondary-variant-ids') || '';
    this.#secondaryVariantIds = raw.split(',').map((s) => Number(s.trim())).filter((n) => n > 0);
  }

  #loadConfigurableProducts() {
    const raw = this.getAttribute('data-configurable-products') || '';
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw);
      this.#configurableProducts = Array.isArray(parsed) ? parsed : [];
    } catch (err) {
      console.warn('[ConfiguratorBundlePdp] Could not parse data-configurable-products.', err);
    }
  }

  #loadFasttrackVariantId() {
    const raw = this.getAttribute('data-fasttrack-variant-id') || '';
    const parsed = Number.parseInt(raw, 10);
    this.#fasttrackVariantId = Number.isNaN(parsed) ? null : parsed;
  }

  #loadDiscountProperty() {
    this.#discountKey = this.getAttribute('data-discount-key') || this.#discountKey;
    this.#discountValue = this.getAttribute('data-discount-value') || this.#discountValue;
  }

  #parseOptionIndex(attrName) {
    const parsed = Number.parseInt(this.getAttribute(attrName) || '', 10);
    return Number.isNaN(parsed) ? -1 : parsed;
  }

  // -- External sync --

  #handleVariantChange = () => {
    if (!this.#baseChildUrl || this._confirmedView?.hasAttribute('hidden')) return;
    this.#continueUrl = this.#resolveUrl(this.#baseChildUrl);
  };
}

if (!customElements.get('configurator-bundle-pdp-component')) {
  customElements.define('configurator-bundle-pdp-component', ConfiguratorBundlePdp);
}
